import React, { useRef } from 'react';
import styled from 'styled-components';
import { IonIcon } from '@ionic/react';
import { happyOutline } from 'ionicons/icons';
import { Icon, Popover, Whisper } from 'rsuite';
import { useButton } from '@react-aria/button';
import { FocusScope, FocusRing } from '@react-aria/focus';

import { Emoji } from './Emoji';
import * as Constants from '../../constants';

const EmojiPickerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 140px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  .btn-emoji {
    font-size: 1.5rem;
    flex: 1 0 0;
    background: rgba(255, 255, 255, 0);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px 6px;
    transition: 0.2s;
    margin: 4px;
    border-radius: 8px;
    outline: 0;
    width: 41px;
    box-sizing: border-box;
    height: 40px;
    flex-grow: 0;

    &:hover,
    &.focus-ring {
      background: #d7ecf1;
    }
    .emoji {
      pointer-events: none;
      position: relative;
      top: 1px;
    }
  }
  .rs-flex-box-grid-item {
    flex-grow: 1;
  }
`;

const OpenEmojisButton = styled.div`
  button {
    color: rgba(var(--ion-color-dark-rgb), 0.6);
    transition: 0.1s;
    display: flex;
    align-items: center;
    justify-content: center;
    background: inherit;

    &:hover,
    &:focus {
      color: rgba(var(--ion-color-dark-rgb), 0.9);
    }
  }
`;

type PickerType =
  | typeof Constants.EMOJI_PICKER_TYPES.PT_USER_COMMENT
  | typeof Constants.EMOJI_PICKER_TYPES.PT_COMMENT_REACTION;

const getEmojis = (type: PickerType): Constants.EmojiProperties[] | false => {
  switch (type) {
    case Constants.EMOJI_PICKER_TYPES.PT_USER_COMMENT:
      return Object.values(Constants.EMOJIS);
    case Constants.EMOJI_PICKER_TYPES.PT_COMMENT_REACTION:
      return Object.values(Constants.REACTION_TYPES).map((reactionType) => {
        return reactionType.emoji;
      });
    default:
      return false;
  }
};

const getIcon = (type: PickerType) => {
  return type === Constants.EMOJI_PICKER_TYPES.PT_COMMENT_REACTION ? (
    <Icon icon="gift" />
  ) : (
    <IonIcon icon={happyOutline} />
  );
};

export const EmojiPicker: React.FC<{
  type: PickerType;
  onSelectEmoji: (emojiCode: string) => void;
}> = ({ onSelectEmoji, type }): React.ReactElement => {
  const whisperRef = useRef<any>(null);
  const allowedEmojis = getEmojis(type);

  const pickerIcon = getIcon(type);

  const EmojiButton = (props: any) => {
    const { emoji, children } = props;
    const emojiButtonRef = useRef<HTMLButtonElement>(null);
    const { buttonProps } = useButton(props, emojiButtonRef);

    const handleEmojiClick = (code: string) => {
      onSelectEmoji(code);
      if (
        whisperRef &&
        whisperRef.current &&
        typeof whisperRef.current.hide !== 'undefined'
      ) {
        whisperRef.current.hide();
      }
    };

    return (
      <FocusRing focusRingClass="focus-ring">
        <button
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...buttonProps}
          ref={emojiButtonRef}
          title={emoji.label}
          type="button"
          className="btn btn-emoji"
          tabIndex={0}
          onClick={(e) => handleEmojiClick(emoji.code)}
        >
          {children}
        </button>
      </FocusRing>
    );
  };
  const speaker = (
    <Popover preventOverflow className="emoji-popover" title="">
      <FocusScope autoFocus contain restoreFocus>
        <EmojiPickerWrapper>
          {allowedEmojis &&
            allowedEmojis.map((emoji: Constants.EmojiProperties) => {
              return (
                <EmojiButton
                  key={emoji.code}
                  aria-label={emoji.label}
                  emoji={emoji}
                >
                  <Emoji emoji={emoji} ariaHidden />
                </EmojiButton>
              );
            })}
        </EmojiPickerWrapper>
      </FocusScope>
    </Popover>
  );
  return (
    <>
      <Whisper
        placement="topEnd"
        trigger={['hover', 'click']}
        triggerRef={whisperRef}
        speaker={speaker}
        enterable
      >
        <OpenEmojisButton>
          <button
            tabIndex={0}
            title="Insert Emoji"
            aria-label="Insert Emoji"
            type="button"
            className="btn btn-emoji"
          >
            {pickerIcon}
          </button>
        </OpenEmojisButton>
      </Whisper>
    </>
  );
};
